<template>
  <div class="decl-root" @keydown="onKeydown">
    <transition name="decl-fade" appear>
      <div class="decl-backdrop" @click="requestClose"></div>
    </transition>

    <transition name="decl-slide" appear>
      <aside
        ref="panel"
        class="decl-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="declaration-title"
        tabindex="-1"
      >
        <header class="decl-head">
          <div>
            <h2 id="declaration-title" class="decl-title">Firmar y enviar solicitud</h2>
            <p class="decl-subtitle">{{ subtitle }}</p>
          </div>
          <button type="button" class="decl-close" aria-label="Cerrar" @click="requestClose">
            <PortalIcon name="x" :size="18" />
          </button>
        </header>

        <div class="decl-body">
          <!-- The same declaration, acceptance and signature as the wizard's last step. -->
          <NewApplicationForm
            ref="form"
            inline
            :section="3"
            :initial-data="candidate"
            :user-email="userEmail"
            @missing="missing = $event"
          />

          <div class="decl-note">
            <PortalIcon name="info" :size="16" class="decl-note-icon" />
            <span>Al enviarla, su solicitud queda registrada y el equipo de Reclutamiento podrá revisarla.</span>
          </div>
        </div>

        <footer class="decl-foot">
          <span v-if="missing > 0" class="decl-foot-text" aria-live="polite">
            {{ missing === 1 ? "Falta 1 campo por completar" : `Faltan ${missing} campos por completar` }}
          </span>
          <button type="button" class="ghost-btn" :disabled="saving" @click="requestClose">Cancelar</button>
          <button type="button" class="solid-btn" :disabled="saving" @click="save">
            {{ saving ? "Enviando…" : "Enviar solicitud" }}
          </button>
        </footer>
      </aside>
    </transition>

    <ConfirmDialog
      v-if="confirmingDiscard"
      destructive
      title="Tiene cambios sin guardar"
      message="Si cierra ahora, perderá la firma y los cambios que hizo."
      confirm-label="Descartar cambios"
      cancel-label="Seguir editando"
      @confirm="discard()"
      @cancel="confirmingDiscard = false"
    />
  </div>
</template>

<script>
import ConfirmDialog from "../ConfirmDialog.vue";
import NewApplicationForm from "../NewApplicationForm.vue";
import PortalIcon from "./PortalIcon.vue";

// The declaration, the last of the four steps, as a modal (a side panel on phones): it opens once Residencia is
// done, and again from the status card's "Firmar y enviar" if it was closed without signing. Signing it sends the
// application (application_step 4). Emits "saved" once sent, and "close".
export default {
  name: "DeclarationModal",
  components: { ConfirmDialog, NewApplicationForm, PortalIcon },
  props: {
    // The /candidate/me response the declaration starts from.
    candidate: { type: Object, required: true },
    userEmail: { type: String, default: "" },
    // Opened right after saving Residencia (and not from the status card): it says so.
    fromResidence: { type: Boolean, default: false },
  },
  data() {
    return {
      saving: false,
      // Required fields still missing after a send attempt.
      missing: 0,
      // Asking whether to throw away the signature and the acceptance before closing.
      confirmingDiscard: false,
      previousBodyOverflow: "",
    };
  },
  computed: {
    subtitle() {
      const last = "lea la declaración jurada, acéptela y firme para enviar su solicitud.";
      return this.fromResidence ? `Se guardó «Residencia». Último paso: ${last}` : `Último paso: ${last}`;
    },
  },
  mounted() {
    // The page behind stays put while the panel scrolls.
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    this.$nextTick(() => {
      if (this.$refs.panel) this.$refs.panel.focus();
    });
  },
  beforeDestroy() {
    document.body.style.overflow = this.previousBodyOverflow;
  },
  methods: {
    requestClose() {
      if (this.saving) return;
      const form = this.$refs.form;
      if (form && form.hasUnsavedChanges()) {
        this.confirmingDiscard = true;
        return;
      }
      this.$emit("close");
    },
    discard() {
      this.confirmingDiscard = false;
      this.$emit("close");
    },
    // Esc closes the modal, unless it's meant for a text field or a notice of the form is open.
    onKeydown(e) {
      if (e.key !== "Escape") return;
      const target = e.target;
      if (target.tagName === "TEXTAREA" || (target.tagName === "INPUT" && target.type !== "checkbox")) return;
      const form = this.$refs.form;
      if (form && form.noticeModal.visible) return;
      this.requestClose();
    },
    save() {
      if (this.saving) return;
      this.saving = true;
      this.$refs.form
        .saveSection()
        .then((saved) => {
          if (saved) this.$emit("saved", 3);
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style scoped>
.decl-root {
  position: fixed;
  inset: 0;
  /* Below the form's own notices (z-index 1000), which open over it, and above the fixed top bar. */
  z-index: 900;
  display: flex;
  justify-content: flex-end;
}
.decl-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(10, 16, 32, 0.55);
}
.decl-panel {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  width: min(780px, 100%);
  height: 100%;
  background: #fff;
  box-shadow: -18px 0 50px -20px rgba(10, 16, 32, 0.5);
  outline: none;
}
.decl-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 22px 28px 18px;
  border-bottom: 1px solid #eceef3;
  -webkit-font-smoothing: antialiased;
}
.decl-title {
  margin: 0;
  font: 600 20px/1.3 "Geist", system-ui, sans-serif;
  letter-spacing: -0.02em;
  color: #0e1424;
}
.decl-subtitle {
  margin: 4px 0 0;
  font: 400 14px/1.5 "Geist", system-ui, sans-serif;
  color: #5d6578;
}
.decl-close {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e3e6ed;
  border-radius: 10px;
  background: #fff;
  color: #6b7283;
  cursor: pointer;
}
.decl-close:hover {
  background: #f4f5f8;
  color: #0e1424;
}
.decl-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 22px 28px 28px;
}
.decl-note {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 18px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #f4f5f8;
  box-shadow: inset 0 0 0 1px #e6e8ee;
  font: 400 13px/1.5 "Geist", system-ui, sans-serif;
  color: #5d6578;
}
.decl-note-icon {
  flex-shrink: 0;
  margin-top: 1px;
}
.decl-foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 28px;
  border-top: 1px solid #eceef3;
  background: #fff;
  -webkit-font-smoothing: antialiased;
}
.decl-foot-text {
  margin-right: auto;
  font: 500 13px/1.4 "Geist", system-ui, sans-serif;
  color: #c42b2b;
}
.ghost-btn,
.solid-btn {
  height: 44px;
  padding: 0 22px;
  border-radius: 12px;
  font: 600 14px "Geist", system-ui, sans-serif;
  cursor: pointer;
}
.ghost-btn {
  border: 1px solid #e3e6ed;
  background: #fff;
  color: #0e1424;
}
.ghost-btn:hover:not(:disabled) {
  background: #f4f5f8;
}
.solid-btn {
  border: none;
  background: #0e1424;
  box-shadow: 0 8px 18px -6px rgba(10, 16, 32, 0.6);
  color: #fff;
}
.solid-btn:hover:not(:disabled) {
  background: #24304d;
}
.ghost-btn:disabled,
.solid-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.decl-fade-enter-active {
  transition: opacity 0.25s ease;
}
.decl-fade-enter {
  opacity: 0;
}
/* The panel ends with no transform: a transformed ancestor would trap the form's fixed notices inside it. */
.decl-slide-enter-active {
  transition: transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
}
.decl-slide-enter {
  transform: translateX(100%);
}
@media (prefers-reduced-motion: reduce) {
  .decl-fade-enter-active,
  .decl-slide-enter-active {
    transition: none;
  }
}

@media (max-width: 640px) {
  .decl-head,
  .decl-body,
  .decl-foot {
    padding-left: 18px;
    padding-right: 18px;
  }
  .decl-foot {
    flex-wrap: wrap;
  }
  .decl-foot-text {
    flex-basis: 100%;
  }
  .decl-foot .ghost-btn,
  .decl-foot .solid-btn {
    flex: 1;
    padding: 0 12px;
  }
}

/* Desktop: a centered modal instead of the side panel (which stays as is on phones and small tablets). */
@media (min-width: 769px) {
  .decl-root {
    align-items: center;
    justify-content: center;
    padding: 24px;
  }
  .decl-panel {
    width: min(860px, 100%);
    height: auto;
    max-height: 100%;
    border-radius: 20px;
    box-shadow: 0 30px 70px -20px rgba(10, 16, 32, 0.55);
  }
  /* A short declaration hugs its content; a long one scrolls inside the modal, header and buttons staying put. */
  .decl-head {
    border-radius: 20px 20px 0 0;
  }
  .decl-foot {
    border-radius: 0 0 20px 20px;
  }
  .decl-slide-enter-active {
    transition: transform 0.25s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.2s ease;
  }
  .decl-slide-enter {
    transform: translateY(16px) scale(0.98);
    opacity: 0;
  }
}
</style>
